/**
 * InjectionDetector - Detección de intentos de inyección en runtime (browser).
 *
 * Solo observa y detecta payloads enviados desde el cliente.
 * NO confirma explotación real. NO reporta eventos (lo hace el reporter).
 * NO sanitiza ni bloquea; solo devuelve resultado de análisis.
 */

/** @typedef {'XSS' | 'SQLI' | 'RCE' | 'PATH_TRAVERSAL'} InjectionType */

/** @typedef {{ detected: true, type: InjectionType, variant: string, evidence: string, confidence?: 'high'|'medium' } | { detected: false }} AnalysisResult */

/**
 * Patterns by type with confidence. We log ALL attempts: any match (1 high or 1 medium) is reported.
 * @typedef {{ id: string, pattern: RegExp, confidence: 'high'|'medium' }} PatternDef
 */
const XSS_PATTERNS = [
	{ id: 'script_tag', pattern: /<script[\s>/]/i, confidence: 'high' },
	{ id: 'javascript_protocol', pattern: /javascript\s*:/i, confidence: 'high' },
	// No word boundary before "on" would match "aria-controls=", "condition=",
	// "configuration=" (the "on" substring lands mid-word). Require "on" not be
	// preceded by a word char or hyphen so it only fires at a genuine attribute
	// boundary — see US #<ADO-ticket> false-positive audit.
	{ id: 'event_handler', pattern: /(?:^|[^\w-])on\w+\s*=/i, confidence: 'high' },
	// data: URI — text/html and JS MIME types both execute scripts in browser context
	{ id: 'data_protocol', pattern: /data\s*:\s*(?:text\/html|text\/javascript|application\/(?:javascript|ecmascript))/i, confidence: 'medium' },
	{ id: 'vbscript', pattern: /vbscript\s*:/i, confidence: 'medium' },
	// Function() constructor — indirect eval used heavily in PATT polyglots and WAF bypass chains:
	// Function('alert(1)')(), []['constructor']['constructor']('alert(1)')()
	// \bFunction\s*\( only matches standalone Function( not myFunction( or _Function(
	{ id: 'function_constructor', pattern: /\bFunction\s*\(\s*['"`]/, confidence: 'medium' },
	// document.write( in request data — DOM sink; almost always injection when sent as user input
	{ id: 'document_write', pattern: /\bdocument\.write\s*\(/i, confidence: 'medium' },
	// setTimeout/setInterval called with a string literal — equivalent to eval()
	{ id: 'timer_eval', pattern: /\b(?:setTimeout|setInterval)\s*\(\s*['"`]/, confidence: 'medium' },
	// React dangerouslySetInnerHTML — if this appears in request data it targets a React DOM sink
	{ id: 'dangerously_set_inner_html', pattern: /dangerouslySetInnerHTML\s*[=:{]/, confidence: 'high' },
	// <iframe srcdoc="..."> injects HTML without a src URL, bypassing src-based CSP rules
	{ id: 'srcdoc_iframe', pattern: /<iframe[^>]+srcdoc\s*=/i, confidence: 'high' },
	// MathML tags used in mXSS bypass chains — sanitizers that allow <math> but strip <script>
	// can be bypassed via <mglyph>/<malignmark> context-switching in innerHTML parsing
	{ id: 'mathml_xss', pattern: /<(?:math|mtext|annotation-xml|mglyph|malignmark)\b/i, confidence: 'medium' },
	// data:image/svg+xml;base64, — base64-encoded SVG with embedded onload/event handlers;
	// not caught by the existing data_protocol pattern which only covers text/html and text/js
	{ id: 'base64_data_uri', pattern: /data\s*:\s*(?:image\/svg\+xml|text\/html|text\/xml|application\/xhtml\+xml)[^,]{0,30};\s*base64,/i, confidence: 'high' },
	// Double-percent-encoded angle brackets and quotes (%253c = <, %2527 = ') — bypasses
	// single-pass URL decoders that check for injection after one decode round
	{ id: 'nested_encoding', pattern: /%25(?:3[cCeE]|27|22|2[fF]|5[cC])/i, confidence: 'high' }
];

const SQLI_PATTERNS = [
	{ id: 'union_select', pattern: /union\s+(all\s+)?select/i, confidence: 'high' },
	{ id: 'or_equals', pattern: /'\s*or\s*'?\d*'\s*=\s*'?/i, confidence: 'medium' },
	{ id: 'or_1_equals_1', pattern: /or\s+1\s*=\s*1(?:\s|$|--|#)/i, confidence: 'medium' },
	{ id: 'or_true', pattern: /or\s+['"]?\s*true\s*['"]?/i, confidence: 'medium' },
	// Requires the actual SQLi comment-out technique (closing a string literal then
	// commenting the rest: 'admin'--) rather than bare "--" (markdown rules, CLI
	// flags, email signatures) — see US #<ADO-ticket> false-positive audit.
	{ id: 'comment_dash', pattern: /'\s*--\s*($|\s)/m, confidence: 'medium' },
	{ id: 'comment_hash', pattern: /#\s*($|\s)/m, confidence: 'medium' },
	// The bare "/*" alternative (no required closing "*/") matched literal "*/*"
	// substrings, including the standard HTTP `Accept: */*` header set by jQuery's
	// $.ajax() default — excluded by requiring the "/*" not be preceded by another
	// "*" — see US #<ADO-ticket> false-positive audit.
	{ id: 'comment_block', pattern: /\/\*[\s\S]*?\*\/|(?:^|[^*])\/\*/, confidence: 'medium' },
	// Tightened to require the digit=digit pair be immediately followed by an
	// end-of-clause marker (comment, terminator, or closing quote/paren); bare
	// "\d+=\d+" matched ordinary version/coordinate strings ("v2=2"). The
	// keyword-driven tautology case ("' OR 5=5") is already covered by
	// boolean_blind_and/or, or_1_equals_1, and or_true — see US #<ADO-ticket>.
	{ id: 'always_true', pattern: /\d+\s*=\s*\d+\s*(?:--|#|\/\*|;|\)|'|")/, confidence: 'medium' },
	{ id: 'concat_or_concat', pattern: /concat\s*\(/i, confidence: 'medium' },
	{ id: 'drop_table', pattern: /\bdrop\s+table\b/i, confidence: 'high' },
	{ id: 'truncate_table', pattern: /\btruncate\s+table\b/i, confidence: 'high' },
	{ id: 'delete_from', pattern: /\bdelete\s+from\b/i, confidence: 'high' },
	{ id: 'termination_drop', pattern: /;\s*(?:drop|delete|truncate)\s+/i, confidence: 'high' },
	{ id: 'sleep_benchmark', pattern: /(?:sleep\s*\(|benchmark\s*\()/i, confidence: 'high' },
	{ id: 'subquery_in', pattern: /\s+in\s*\(\s*select\s+/i, confidence: 'medium' },
	{ id: 'hex_char', pattern: /\b(?:0x[0-9a-f]+|char\s*\()/i, confidence: 'medium' },
	// Requires a following quote/digit so genuine tautology syntax (' or '1'='1)
	// is still caught while natural English possessive+conjunction text ("men's
	// and women's") no longer matches — see US #<ADO-ticket> false-positive audit.
	{ id: 'single_quote_escape', pattern: /'\s*(?:or|and)\s+(?:['"]|\d)/i, confidence: 'medium' },
	{ id: 'double_dash', pattern: /--\s*$|--\s*[^\s]/m, confidence: 'medium' },
	// Tightened the "%...or/and" alternative to require the or/and be followed
	// by a quote or digit (the actual SQL-tautology shape, e.g. "% OR 1=1");
	// bare "% or/and <word>" matched ordinary percentage text ("50% or more",
	// "70% and rising"). A preceding-quote requirement was considered and
	// rejected — real unquoted payloads like "x% OR 1=1" have no quote before
	// the "%" either, so that would have regressed true-positive detection.
	// The " like ['\"]%" alternative is unchanged (already specific SQL syntax).
	// See US #<ADO-ticket> false-positive audit.
	{ id: 'like_wildcard', pattern: /%\s*(?:or|and)\s+(?:['"]|\d)|\s+like\s+['"]%/i, confidence: 'medium' },
	{ id: 'waitfor_delay', pattern: /waitfor\s+delay\s+/i, confidence: 'high' },
	{ id: 'exec_xp_cmdshell', pattern: /exec\s+(?:master\.)?xp_cmdshell/i, confidence: 'high' },
	{ id: 'information_schema', pattern: /information_schema\./i, confidence: 'medium' },
	// Block 6 — SQLi depth expansion
	// Error-based injection (MySQL): functions that trigger verbose DB errors containing data
	{ id: 'extractvalue_inject', pattern: /extractvalue\s*\(/i, confidence: 'high' },
	{ id: 'updatexml_inject', pattern: /updatexml\s*\(/i, confidence: 'high' },
	{ id: 'floor_rand', pattern: /floor\s*\(\s*rand\s*\(/i, confidence: 'high' },
	{ id: 'exp_tilde', pattern: /exp\s*\(\s*~\s*\(/i, confidence: 'high' },
	// Oracle-specific: string concatenation with chr() to obfuscate payloads; DBMS_PIPE for time-delay
	{ id: 'oracle_chr_concat', pattern: /'\s*\|\|\s*chr\s*\(/i, confidence: 'high' },
	{ id: 'dbms_pipe', pattern: /\bdbms_pipe\.receive_message\s*\(/i, confidence: 'high' },
	// MSSQL-specific: OPENROWSET for OOB data exfiltration; BULK INSERT for file read; xp_regread for registry
	{ id: 'openrowset', pattern: /\bopenrowset\s*\(/i, confidence: 'high' },
	{ id: 'bulk_insert', pattern: /\bbulk\s+insert\b/i, confidence: 'high' },
	{ id: 'xp_regread', pattern: /\bxp_regread\b/i, confidence: 'high' },
	// PostgreSQL: time-based blind injection via pg_sleep()
	{ id: 'pg_sleep', pattern: /\bpg_sleep\s*\(/i, confidence: 'high' },
	// Tautology extensions: single/double-quoted variants of classic ' OR 'x'='x bypass
	{ id: 'or_x_equals_x', pattern: /'\s*or\s+'[a-z0-9]+'\s*=\s*'[a-z0-9]+'/i, confidence: 'medium' },
	{ id: 'double_quote_tautology', pattern: /"\s*or\s+"[a-z0-9]+"\s*=\s*"[a-z0-9]+"/i, confidence: 'medium' },
	// MySQL file read/write — direct filesystem access via SQL
	{ id: 'load_file', pattern: /\bload_file\s*\(/i, confidence: 'high' },
	// INTO OUTFILE with a web-shell extension — SQL-to-RCE; placed before into_outfile to take priority
	{ id: 'outfile_webshell_pattern', pattern: /\binto\s+(?:outfile|dumpfile)\s+['"][^'"]{0,200}\.(?:php\d?|aspx?|jspx?|cgi|sh|py|rb|pl)\b/i, confidence: 'high' },
	{ id: 'into_outfile', pattern: /\binto\s+(?:outfile|dumpfile)\b/i, confidence: 'high' },
	// Blind injection via numeric expressions (all DB engines)
	{ id: 'order_by_num', pattern: /\border\s+by\s+\d+\b/i, confidence: 'medium' },
	{ id: 'having_inject', pattern: /\bhaving\s+\d+\s*=\s*\d+/i, confidence: 'medium' },
	{ id: 'case_when_inject', pattern: /\bcase\s+when\s+\d+\s*=\s*\d+/i, confidence: 'medium' },
	// Oracle OOB exfiltration packages — UTL_HTTP, UTL_FILE, UTL_TCP, UTL_INADDR (DNS lookup), UTL_MAIL/SMTP/RAW
	{ id: 'utl_http_oracle', pattern: /\butl_(?:http|file|tcp|inaddr|mail|smtp|raw)\b/i, confidence: 'high' },
	// MSSQL dynamic SQL execution via stored procedure
	{ id: 'sp_executesql', pattern: /\bsp_executesql\b/i, confidence: 'high' },
	// Stacked queries: ; SELECT/INSERT/UPDATE/EXEC — multi-statement execution after terminator
	{ id: 'stacked_queries', pattern: /;\s*(?:select|insert|update|exec(?:ute)?|call|declare|set\s+@)\b/i, confidence: 'high' },
	// Boolean-based blind AND: infers data by observing true vs. false server responses
	{ id: 'boolean_blind_and', pattern: /\bAND\s+(?:['"]?\s*)?\d+\s*[<>!=]+\s*\d+/i, confidence: 'medium' },
	// Boolean-based blind OR: infers data by observing true vs. false server responses
	{ id: 'boolean_blind_or', pattern: /\bOR\s+(?:['"]?\s*)?\d+\s*[<>!=]+\s*\d+/i, confidence: 'medium' },
	// MySQL version-gated comment: /*!50000 UNION SELECT */ — bypasses WAF signature matching
	{ id: 'mysql_version_comment', pattern: /\/\*!\d*\s*(?:select|union|drop|insert|exec|or|and)\b/i, confidence: 'high' },
	// MSSQL WAITFOR TIME — time-based blind variant distinct from WAITFOR DELAY
	{ id: 'mssql_waitfor_time', pattern: /\bwaitfor\s+time\s+['"]/i, confidence: 'high' },
	// PostgreSQL COPY ... TO/FROM PROGRAM — arbitrary OS command execution via SQL
	{ id: 'postgres_copy_program', pattern: /\bcopy\b[^;]{0,60}\b(?:to|from)\s+program\b/i, confidence: 'high' }
];

// Shared denylist of recognizable shell/interpreter command words, used to
// narrow the generic operator-based RCE patterns below (pipe_cmd, semicolon_cmd,
// and_operator, or_operator) so they require a real command word rather than any
// following word — see US #<ADO-ticket> false-positive audit. More specific
// attack-chain patterns (curl_wget_chain, nc_reverse_shell, interpreter_inject,
// windows_cmd, etc.) remain unchanged and are not limited to this list.
const CMD_WORD = '(?:sh|bash|cat|nc|curl|wget|python3?|perl|tee|grep|awk|sed|base64|xargs|powershell|cmd)\\b';

const RCE_PATTERNS = [
	// Bare "|\w+" matched any pipe-delimited text ("Option A | Option B"). Requires
	// a recognizable command word after the pipe.
	{ id: 'pipe_cmd', pattern: new RegExp('\\|\\s*' + CMD_WORD, 'i'), confidence: 'medium' },
	{ id: 'backtick', pattern: /`[^`]*`/, confidence: 'medium' },
	// Excludes known jQuery call shapes ($('#id'), $(".class"), $(document),
	// $(window), $(this)) via negative lookahead, while leaving real command
	// substitution ($(whoami), $(cat /etc/passwd)) detectable — see US #<ADO-ticket>
	// false-positive audit (jQuery is used throughout this app's own frontend).
	{ id: 'dollar_paren', pattern: /\$\((?!['"]?(?:document|window|this)\)|['"][#.<])[^)]+\)/i, confidence: 'high' },
	// Bare ";\w+" matched any semicolon-separated text. Requires a recognizable
	// command word after the semicolon.
	{ id: 'semicolon_cmd', pattern: new RegExp(';\\s*' + CMD_WORD, 'i'), confidence: 'medium' },
	{ id: 'eval_call', pattern: /eval\s*\(/i, confidence: 'high' },
	// Shell logical operators — medium confidence (legitimate query strings may contain these).
	// Requires a recognizable command word after the operator (see CMD_WORD above).
	{ id: 'and_operator', pattern: new RegExp('&&\\s*' + CMD_WORD, 'i'), confidence: 'medium' },
	{ id: 'or_operator', pattern: new RegExp('\\|\\|\\s*' + CMD_WORD, 'i'), confidence: 'medium' },
	// URL-encoded && and || — high confidence; double-encoding these is almost always intentional injection
	{ id: 'encoded_and', pattern: /%26%26/i, confidence: 'high' },
	{ id: 'encoded_pipe', pattern: /%7c%7c/i, confidence: 'high' },
	// Newline/CR injection — separator in log injection and shell command chains
	{ id: 'newline_inject', pattern: /(%0a|%0d)\w/i, confidence: 'high' },
	// Windows command execution patterns
	{ id: 'windows_cmd', pattern: /\bcmd(?:\.exe)?\s+\/[cCkK]\b/i, confidence: 'high' },
	// Requires an invocation flag (-enc, -Command, /c, etc.) rather than the bare
	// word, since this is a security product whose own search/content legitimately
	// mentions "PowerShell" as a topic word in outbound search/filter requests —
	// see US #<ADO-ticket> false-positive audit.
	{ id: 'powershell', pattern: /\bpowershell(?:\.exe)?\s+[-\/][a-z]/i, confidence: 'high' },
	// Shell environment variable expansion used to bypass space/metachar filters
	{ id: 'env_expansion', pattern: /\$\{IFS\}|\$IFS(?=[^a-zA-Z_]|$)/i, confidence: 'medium' },
	// Shell interpreter invocation: bash -c, sh -c, python -c, perl -e, etc.
	// Sending an interpreter flag in a request parameter is almost always injection.
	{ id: 'interpreter_inject', pattern: /\b(?:bash|sh|ksh|zsh|python3?|perl|ruby|php|node(?:js)?)\s+-[ce]\b/i, confidence: 'high' },
	// CRLF sequence — %0d%0a as HTTP response splitting vector and shell command separator.
	// Distinct from newline_inject (%0a\w / %0d\w) which requires a following word char.
	{ id: 'crlf_inject', pattern: /%0d%0a/i, confidence: 'high' },
	// Nested subshell $($(cmd)) — double evaluation used in obfuscation chains to bypass static filters
	{ id: 'subshell_nested', pattern: /\$\(\s*\$\(/, confidence: 'high' },
	// curl/wget fetching an external HTTP URL — arbitrary payload download for chained execution
	{ id: 'curl_wget_chain', pattern: /\b(?:curl|wget)\s+(?:-\S+\s+)*https?:\/\//i, confidence: 'high' },
	// Netcat reverse shell: nc/ncat -e /bin/sh or nc <ip> <port> (with -l/-e flag)
	{ id: 'nc_reverse_shell', pattern: /\b(?:nc|ncat|netcat)\b[^\n]{0,60}-[el]\b/i, confidence: 'high' },
	// Windows certutil LOLBin — used for URL download and base64 decode of payloads
	{ id: 'certutil_download', pattern: /\bcertutil\b/i, confidence: 'high' },
	// nslookup in a request parameter — indicator of DNS-based data exfiltration probe
	{ id: 'dns_exfil_pattern', pattern: /\bnslookup\b/i, confidence: 'high' }
];

/** Path traversal: ../, ..\, encoded variants, bypass techniques. Un match = detected. */
const PATH_TRAVERSAL_PATTERNS = [
	// Left unchanged: this detector only runs on outbound request URL/body/headers
	// (via analyzeRequest/analyzeOnLoad), never on arbitrary DOM/page text — a
	// scan-report table merely displaying "../config/x.php" is never scanned. No
	// evidence found of a feature that round-trips a raw file path back out via
	// fetch/XHR. Reviewed in the US #<ADO-ticket> false-positive audit; monitor
	// real telemetry (meta.confidence) post-rollout before changing this rule.
	{ id: 'parent_dir_slash', pattern: /\.\.(\/|\\|%2f|%2F|%5c|%5C)/i },
	{ id: 'double_encoded', pattern: /%2e%2e(%2f|%5c)/i },
	// Triple percent-encoding: %252e%252e%252f — bypasses single-pass URL decoders
	{ id: 'triple_encoded', pattern: /%252e%252e(%252f|%255c)/i },
	// Null byte injection: ../ followed by %00 — truncates filenames in older runtimes
	{ id: 'null_byte', pattern: /\.\.(?:\/|\\|%2f|%5c)[^?#]*?%00/i },
	// Semicolon path bypass: ..;/ — exploits Tomcat/Java servlet path normalization
	{ id: 'semicolon_bypass', pattern: /\.\.;[\/\\]/ },
	// UTF-8 overlong encoding of '.' (%c0%ae) or '\' (%c1%9c) — illegal UTF-8, always injection
	{ id: 'utf8_overlong', pattern: /%c0%ae|%c1%9c|%e0%80%ae/i },
	// UTF-8 overlong encoding of '/' (%c0%af / %e0%80%af) — complements %c0%ae for '.';
	// bypasses path filters that decode only standard ASCII before checking for traversal
	{ id: 'utf8_overlong_slash', pattern: /%c0%af|%e0%80%af/i },
	// Windows four-dot bypass: ..../ or ....\  — normalized to ../ by Windows path resolver;
	// bypasses filters that only strip two-dot sequences
	{ id: 'four_dot_slash', pattern: /\.{4}[\/\\]/ },
	// Mixed slash traversal: ..\ / or ../ \ — bypasses filters that only check one slash type
	{ id: 'mixed_slash_traversal', pattern: /\.\.(?:\/\\|\\\/|%2f%5c|%5c%2f)/i },
	// IIS-style Unicode dot encoding: %u002e%u002e — bypasses standard percent-decode only decoders
	{ id: 'unicode_dot_variant', pattern: /%u002e%u002e(?:%u002f|%u005c|\/|\\)/i },
	// Explicit Windows System32 directory — direct system path target in request data
	{ id: 'windows_system32_target', pattern: /windows[\/\\]system32[\/\\]/i },
	// Linux /proc/self or /proc/<pid> — process environment/memory/fd leakage paths
	{ id: 'proc_self_access', pattern: /\/proc\/(?:self|\d+)\/(?:environ|cmdline|fd|maps|mem|status|exe)\b/i },
	// Classic sensitive Unix config files — /etc/passwd, /etc/shadow, /etc/hosts, etc.
	{ id: 'passwd_file_access', pattern: /[\/\\]etc[\/\\](?:passwd|shadow|group|hosts|hostname|issue|os-release)\b/i }
];

/**
 * Registra TODOS los intentos: devuelve el primer match (prioridad: high, luego medium).
 * @param {string} input
 * @param {PatternDef[]} patterns
 * @returns {{ variant: string, evidence: string, confidence: string } | null}
 */
function matchAnySingle(input, patterns) {
	if (!input || typeof input !== 'string') return null;
	let firstHigh = null;
	let firstMedium = null;
	for (const { id, pattern, confidence } of patterns) {
		const m = input.match(pattern);
		if (m && m[0]) {
			// evidence is the full analyzed input, not the regex match (m[0]) —
			// patterns are intentionally narrow trigger signatures (e.g. script_tag
			// only needs "<script>"), so using m[0] as evidence silently discarded
			// the rest of the actual payload. Downstream sanitizeEvidence() still
			// caps the length before this leaves the browser.
			const hit = { variant: id, evidence: input, confidence };
			if (confidence === 'high') {
				if (!firstHigh) firstHigh = hit;
			} else if (!firstMedium) {
				firstMedium = hit;
			}
		}
	}
	return firstHigh || firstMedium || null;
}

function matchPathTraversal(input) {
	if (!input || typeof input !== 'string') return null;
	for (const { id, pattern } of PATH_TRAVERSAL_PATTERNS) {
		const m = input.match(pattern);
		if (m && m[0]) return { variant: id, evidence: input };
	}
	return null;
}

function safeDecodeURIComponent(value) {
	if (!value || typeof value !== 'string') return null;
	if (value.indexOf('%') === -1 && value.indexOf('+') === -1) return null;
	try {
		const normalized = value.indexOf('+') !== -1 ? value.replace(/\+/g, ' ') : value;
		const decoded = decodeURIComponent(normalized);
		return decoded !== value ? decoded : null;
	} catch {
		return null;
	}
}

export class InjectionDetector {
	constructor() {
		this._xssPatterns = [...XSS_PATTERNS];
		this._sqliPatterns = [...SQLI_PATTERNS];
		this._rcePatterns = [...RCE_PATTERNS];
		this._pathTraversalPatterns = [...PATH_TRAVERSAL_PATTERNS];
	}

	/**
	 * Detecta patrones XSS. Cualquier match se reporta.
	 */
	detectXSS(input) {
		const hit = matchAnySingle(input, this._xssPatterns);
		if (hit) return { detected: true, type: 'XSS', variant: hit.variant, evidence: hit.evidence, confidence: hit.confidence };
		return { detected: false };
	}

	/**
	 * Detecta patrones SQLi. Cualquier match se reporta.
	 */
	detectSQLi(input) {
		const hit = matchAnySingle(input, this._sqliPatterns);
		if (hit) return { detected: true, type: 'SQLI', variant: hit.variant, evidence: hit.evidence, confidence: hit.confidence };
		return { detected: false };
	}

	/**
	 * Detecta patrones RCE/command-injection. Cualquier match se reporta.
	 */
	detectRCE(input) {
		const hit = matchAnySingle(input, this._rcePatterns);
		if (hit) return { detected: true, type: 'RCE', variant: hit.variant, evidence: hit.evidence, confidence: hit.confidence };
		return { detected: false };
	}

	/**
	 * Detecta intentos de path traversal (../, ..\, encoded).
	 */
	detectPathTraversal(input) {
		const hit = matchPathTraversal(input);
		if (hit) return { detected: true, type: 'PATH_TRAVERSAL', variant: hit.variant, evidence: hit.evidence, confidence: 'high' };
		return { detected: false };
	}

	/**
	 * Analiza una cadena con todos los detectores. Cualquier match se reporta. Prioridad: XSS, SQLi, RCE, PATH_TRAVERSAL.
	 * @param {string} input
	 * @returns {AnalysisResult}
	 */
	_analyzeString(input) {
		const xss = this.detectXSS(input);
		if (xss.detected) return xss;
		const sqli = this.detectSQLi(input);
		if (sqli.detected) return sqli;
		const rce = this.detectRCE(input);
		if (rce.detected) return rce;
		const pathT = this.detectPathTraversal(input);
		if (pathT.detected) return pathT;
		return { detected: false };
	}

	/**
	 * Analiza en contexto "on load" (p. ej. URL actual).
	 * @param {{ url: string }} options
	 * @returns {AnalysisResult}
	 */
	analyzeOnLoad({ url }) {
		if (!url || typeof url !== 'string') return { detected: false };
		const res = this._analyzeString(url);
		if (res.detected) return res;
		const decoded = safeDecodeURIComponent(url);
		if (decoded) return this._analyzeString(decoded);
		return { detected: false };
	}

	/**
	 * Analiza una petición (URL, body, headers). No bloquea; solo devuelve resultado.
	 * @param {{ url?: string, method?: string, headers?: Record<string, string>, body?: string | object, contentType?: string }} options
	 * @returns {AnalysisResult}
	 */
	analyzeRequest({ url, method, headers, body }) {
		const inputs = [];
		if (url && typeof url === 'string') {
			inputs.push(url);
			const decodedUrl = safeDecodeURIComponent(url);
			if (decodedUrl) inputs.push(decodedUrl);
		}
		if (method && typeof method === 'string') inputs.push(method);
		// Headers are intentionally still scanned (decision reviewed in the
		// US #<ADO-ticket> false-positive audit): this is the only surface where a
		// custom header reflecting attacker/user input would be detectable, and no
		// sibling detector (SSRF/OpenRedirect/NoSQL/PrototypePollution) scans
		// headers at all, so this is already the narrowest-scope surface. The
		// comment_block false-positive this caused (default Accept header) was
		// fixed at the pattern level instead — see SQLI_PATTERNS.comment_block above.
		if (headers && typeof headers === 'object') {
			const headerStr = typeof headers === 'string' ? headers : Object.values(headers).join(' ');
			inputs.push(headerStr);
		}
		if (body !== undefined && body !== null) {
			if (typeof body === 'string') {
				inputs.push(body);
				const decodedBody = safeDecodeURIComponent(body);
				if (decodedBody) inputs.push(decodedBody);
			}
			else if (typeof body === 'object') {
				try { inputs.push(JSON.stringify(body)); } catch (_) { inputs.push(String(body)); }
			}
		}

		for (const input of inputs) {
			const result = this._analyzeString(input);
			if (result.detected) return result;
		}

		return { detected: false };
	}
}
